import React, { useCallback, useEffect, useState } from "react";
import { Search, X, CheckCircle2, Package2 } from "lucide-react";
import { getAllBOMs } from "../../../store/slices/bomSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../../store";

interface Bom {
  _id: string;
  name: string;
  description?: string;
}

interface BomSelectionModalProps {
  open: boolean;
  oldBomId?: string | null;
  onClose: () => void;
  onSelect: (data: { selectedBomId: string; oldBomId?: string | null }) => void;
}

const BomSelectionModal: React.FC<BomSelectionModalProps> = ({
  open,
  oldBomId,
  onClose,
  onSelect,
}) => {
  const dispatch = useDispatch<RootDispatch>();

  const { boms, loading } = useSelector(
    (state: RootState) => state.billOfMaterials,
  );

  const [selectedBomId, setSelectedBomId] = useState<string>(oldBomId || "");

  const [search, setSearch] = useState("");

  useEffect(() => {
    if (open) {
      fetchBoms();
    }
  }, [open, search]);

  const fetchBoms = useCallback(async () => {
    try {
      await dispatch(getAllBOMs(search));
    } catch (error) {
      console.error("Failed to load BOMs", error);
    }
  }, [dispatch, search]);

  const handleSubmit = () => {
    if (!selectedBomId) {
      alert("Please select a BOM");
      return;
    }

    onSelect({
      selectedBomId,
      oldBomId: oldBomId || null,
    });

    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="w-full max-w-2xl rounded-3xl bg-white shadow-[0_20px_80px_rgba(0,0,0,0.25)] overflow-hidden animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="border-b border-slate-100 px-6 py-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold text-slate-900">Select BOM</h2>

              <p className="mt-1 text-sm text-slate-500">
                Choose a Bill Of Materials for this order
              </p>
            </div>

            <button
              onClick={onClose}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition hover:bg-slate-200 hover:text-slate-700"
            >
              <X size={18} />
            </button>
          </div>

          {/* Search */}
          <div className="relative mt-5">
            <Search
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
            />

            <input
              type="text"
              placeholder="Search BOM..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 pl-11 pr-4 text-sm outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
            />
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          <div className="h-[420px] overflow-y-auto rounded-2xl border border-slate-200 bg-slate-50/50 p-2">
            {loading ? (
              <div className="space-y-3">
                {[...Array(6)].map((_, index) => (
                  <div
                    key={index}
                    className="animate-pulse rounded-2xl border border-slate-100 bg-white p-4"
                  >
                    <div className="h-4 w-1/3 rounded bg-slate-200" />

                    <div className="mt-3 h-3 w-2/3 rounded bg-slate-100" />
                  </div>
                ))}
              </div>
            ) : boms.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-100">
                  <Package2 size={28} className="text-slate-400" />
                </div>

                <h3 className="mt-4 text-lg font-semibold text-slate-800">
                  No BOMs Found
                </h3>

                <p className="mt-1 text-sm text-slate-500">
                  Try searching with another keyword
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {boms.map((bom) => {
                  const selected = selectedBomId === bom._id;

                  return (
                    <button
                      key={bom._id}
                      type="button"
                      onClick={() => setSelectedBomId(bom?._id || "")}
                      className={`group relative w-full rounded-2xl border p-4 text-left transition-all duration-200 ${
                        selected
                          ? "border-blue-500 bg-blue-50 shadow-md shadow-blue-100"
                          : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <div
                              className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                                selected ? "bg-blue-100" : "bg-slate-100"
                              }`}
                            >
                              <Package2
                                size={18}
                                className={
                                  selected ? "text-blue-600" : "text-slate-500"
                                }
                              />
                            </div>

                            <div className="min-w-0">
                              <h3 className="truncate text-sm font-semibold text-slate-900">
                                {bom.name}
                              </h3>

                              {bom.description && (
                                <p className="mt-1 line-clamp-2 text-xs text-slate-500">
                                  {bom.description}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="shrink-0">
                          {selected ? (
                            <CheckCircle2 size={22} className="text-blue-600" />
                          ) : (
                            <div className="h-5 w-5 rounded-full border-2 border-slate-300 transition group-hover:border-slate-400" />
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-slate-100 bg-slate-50 px-6 py-5">
          <button
            onClick={onClose}
            className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            disabled={!selectedBomId}
            className="rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-200 transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none"
          >
            Select BOM
          </button>
        </div>
      </div>
    </div>
  );
};

export default BomSelectionModal;
